import { useEffect, useRef, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast } from '../Toast/toastStore';
import { t, currentLanguage } from '../../i18n/i18n';
import './CopyButton.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/**
 * The label as it reads mid-sentence: lower-cased in English ("Copy login
 * id"), as it always was; a translation keeps its own casing, since German
 * capitalises its nouns and an "ID" should stay one.
 */
export const lowerLabel = (label) =>
  currentLanguage() === 'en' ? String(label).toLowerCase() : t(label);

/** How long the tick stays before the copy glyph comes back — CopyPill's beat. */
const HELD_FOR = 1000;

/**
 * Copy affordance used beside credentials and login IDs — 1:9553.
 * 20px hit area, 12px glyph, r4.
 *
 * `label` names the thing in the toast: "Login ID copied".
 *
 * A copy also answers where it was clicked (N105): the glyph turns into a
 * green tick for a second, as CopyPill's "Copied" chip does. The toast alone
 * lands in the far corner of the screen, away from a 20px target, and is easy
 * to miss — which made the copy look like it did nothing.
 */
export default function CopyButton({ value, label = 'Value', size = 12, className = '' }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  // Cleared on unmount, so a copy just before a route change can't set state
  // on a button that has gone.
  useEffect(() => () => clearTimeout(timer.current), []);

  const onCopy = (_text, ok) => {
    if (!ok) {
      toast.error(t('Could not copy {{label}}', { label: lowerLabel(label) }));
      return;
    }
    toast.success(t('{{label}} copied', { label: t(label) }));
    setCopied(true);
    // Restarted rather than stacked, so a second copy holds the tick for a
    // second from the second click.
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), HELD_FOR);
  };

  return (
    <CopyToClipboard text={String(value)} onCopy={onCopy}>
      <button
        type="button"
        className={`copy-btn${copied ? ' is-copied' : ''} ${className}`.trim()}
        // Constant, so a screen reader names the control once; the toast is
        // what announces the copy.
        aria-label={t('Copy {{label}}', { label: lowerLabel(label) })}
        title={
          copied
            ? t('{{label}} copied', { label: t(label) })
            : t('Copy {{label}}', { label: lowerLabel(label) })
        }
      >
        <span
          className="icon copy-btn__icon"
          style={{ ...icon(copied ? 'check-sm' : 'copy'), width: size, height: size }}
        />
      </button>
    </CopyToClipboard>
  );
}
